import './comment.css';
import { Comment as CommentType, Chapter } from '@novelizing/types';
import { User } from '@novelizing/types';
import { useState, useEffect } from 'preact/hooks';
import { useLocation } from 'wouter';
import { CommentForm } from '../comment-form';

interface CommentProps {
  comment: CommentType;
  userInfo?: User;
  childComments?: CommentType[];
  userInfoMap: Record<number, User | undefined>;
  commentMap: Record<string, CommentType[]>;
  onReply: (content: string, userId: number, parentId: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
  onUpdate: (commentId: string, content: string) => Promise<void>;
  user?: User;
  chapter?: Chapter;
  level?: number;
}

// Utility function moved here
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return (
    date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }) +
    ' ' +
    date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    })
  );
};

export const Comment = ({
  comment,
  userInfo,
  childComments = [],
  userInfoMap,
  commentMap,
  onReply,
  onDelete,
  onUpdate,
  user,
  chapter,
  level = 0,
}: CommentProps) => {
  const [location] = useLocation();
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);

  // Check if this comment's ID matches the URL hash
  useEffect(() => {
    // Get the hash from the URL (remove the # character)
    const hash = window.location.hash.substring(1);

    // Set highlighted state if this comment's ID matches the hash
    setIsHighlighted(hash === comment.id);

    // If this comment is highlighted, scroll to it
    if (hash === comment.id) {
      // Use a small timeout to ensure the DOM is ready
      setTimeout(() => {
        const element = document.getElementById(comment.id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [comment.id, location]);

  let userName = 'Unknown User'; // Default to Unknown
  if (userInfo) {
    userName = userInfo.name;
  } else if (comment.userId) {
    // If userInfo is not yet loaded but we have a userId, show loading
    userName = 'Loading user...';
  }

  const handleReply = async (content: string): Promise<void> => {
    if (user?.id) {
      await onReply(content, user.id, comment.id);
      setShowReplyForm(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (
      confirm(`Are you sure you want to delete this comment and its children?`)
    ) {
      await onDelete(comment.id);
    }
  };

  const handleEdit = async (): Promise<void> => {
    setIsEditing(true);
    setEditContent(comment.content);
  };

  const handleUpdate = async (): Promise<void> => {
    if (editContent.trim()) {
      await onUpdate(comment.id, editContent);
      setIsEditing(false);
    }
  };

  const handleCancelEdit = (): void => {
    setIsEditing(false);
    setEditContent(comment.content);
  };

  // Check if user owns the comment or the chapter
  const userOwnsComment = user?.id === comment.userId;
  // Check if the user owns the chapter
  const userOwnsChapter = user?.id && chapter && user.id === chapter.user?.id;

  // Maximum nesting level to prevent excessive indentation
  const maxLevel = 5;
  const currentLevel = Math.min(level, maxLevel);

  // Define colors for different nesting levels
  const nestingColors = [
    'transparent', // Level 0 (root)
    'var(--primary-focus)', // Level 1
    'var(--secondary-focus)', // Level 2
    'var(--contrast-focus)', // Level 3
    'var(--primary)', // Level 4
    'var(--secondary)', // Level 5+
  ];

  const borderColor =
    nestingColors[Math.min(currentLevel, nestingColors.length - 1)];

  return (
    <div className="comment-thread">
      <div
        key={comment.id}
        className={`comment ${isHighlighted ? 'highlighted' : ''}`}
        id={comment.id}
        style={{
          marginLeft: `${currentLevel * 1}rem`,
          borderLeftColor: borderColor,
        }}
      >
        <div className="comment-body">
          <div className="comment-author">{userName}</div>
          <div className="comment-date">{formatDate(comment.createdAt)}</div>
          <div className="comment-content">
            {isEditing ? (
              <div className="edit-form">
                <textarea
                  value={editContent}
                  onInput={(e) => setEditContent((e.target as HTMLTextAreaElement).value)}
                  className="edit-textarea"
                />
                <div className="edit-actions">
                  <button className="save-button" onClick={handleUpdate}>
                    Save
                  </button>
                  <button className="cancel-button" onClick={handleCancelEdit}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              comment.content
            )}
          </div>
        </div>

        {user?.id && (
          <div className="comment-actions">
            {/* Show delete button if user owns comment or chapter */}
            {(userOwnsComment || userOwnsChapter) && (
              <button className="delete-button" onClick={handleDelete}>
                Delete
              </button>
            )}
            {/* Show edit button only if user owns the comment */}
            {userOwnsComment && !isEditing && (
              <button className="edit-button" onClick={handleEdit}>
                Edit
              </button>
            )}
            <button
              className="reply-button"
              onClick={() => setShowReplyForm(!showReplyForm)}
            >
              {showReplyForm ? 'Cancel' : 'Reply'}
            </button>
          </div>
        )}

        {showReplyForm && (
          <div className="reply-form">
            <CommentForm
              user={user}
              loading={false}
              onSubmit={handleReply}
              placeholder="Write a reply..."
              buttonText="Reply"
            />
          </div>
        )}
      </div>

      {/* Render child comments */}
      {childComments.length > 0 && (
        <div className="child-comments">
          {childComments.map((childComment) => {
            const childUserIdNum = parseInt(childComment.userId.toString(), 10);
            const childUserInfo = !isNaN(childUserIdNum)
              ? userInfoMap[childUserIdNum]
              : undefined;

            // Get nested child comments for this child comment
            const nestedChildComments = commentMap[childComment.id] || [];

            return (
              <Comment
                key={childComment.id}
                comment={childComment}
                userInfo={childUserInfo}
                userInfoMap={userInfoMap}
                commentMap={commentMap}
                onReply={onReply}
                onDelete={onDelete}
                onUpdate={onUpdate}
                user={user}
                chapter={chapter}
                level={level + 1}
                childComments={nestedChildComments}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
